import { BaseClient } from '../client/BaseClient';
import {
  Webhook,
  CreateWebhookRequest,
  UpdateWebhookRequest,
  WebhookTestResult,
  ApiResponse,
} from '../types';

export class Webhooks {
  constructor(private client: BaseClient) {}

  /**
   * Get all webhooks for a session
   * @param tel - Phone number
   */
  async list(tel: string): Promise<Webhook[]> {
    const response = await this.client['get']<{
      success: boolean;
      data: Webhook[];
    }>(`/api/sessions/${encodeURIComponent(tel)}/webhooks`);
    
    return response.data;
  }

  /**
   * Get webhook details
   * @param tel - Phone number
   * @param webhookId - Webhook ID
   */
  async get(tel: string, webhookId: string): Promise<Webhook> {
    const response = await this.client['get']<{
      success: boolean;
      data: Webhook;
    }>(`/api/sessions/${encodeURIComponent(tel)}/webhooks/${webhookId}`);
    
    return response.data;
  }

  /**
   * Create a new webhook for a session
   * @param tel - Phone number
   * @param data - Webhook creation data
   */
  async create(tel: string, data: Omit<CreateWebhookRequest, 'tel'>): Promise<Webhook & { secret: string }> {
    const response = await this.client['post']<{
      success: boolean;
      data: Webhook & { secret: string };
    }>(`/api/sessions/${encodeURIComponent(tel)}/webhooks`, {
      ...data,
      tel,
    });
    
    return response.data;
  }

  /**
   * Update webhook details
   * @param tel - Phone number
   * @param webhookId - Webhook ID
   * @param data - Update data
   */
  async update(
    tel: string, 
    webhookId: string, 
    data: Omit<UpdateWebhookRequest, 'tel' | 'webhookId'>
  ): Promise<Webhook> {
    const response = await this.client['put']<{
      success: boolean;
      data: Webhook;
    }>(`/api/sessions/${encodeURIComponent(tel)}/webhooks/${webhookId}`, data);
    
    return response.data;
  }

  /**
   * Delete a webhook
   * @param tel - Phone number
   * @param webhookId - Webhook ID
   */
  async delete(tel: string, webhookId: string): Promise<{ message: string }> {
    const response = await this.client['delete']<ApiResponse<never>>(
      `/api/sessions/${encodeURIComponent(tel)}/webhooks/${webhookId}`
    );
    return { message: response.message || 'Webhook deleted successfully' };
  }

  /**
   * Test a webhook
   * @param tel - Phone number
   * @param webhookId - Webhook ID
   */
  async test(tel: string, webhookId: string): Promise<WebhookTestResult> {
    return this.client['post']<WebhookTestResult>(
      `/api/sessions/${encodeURIComponent(tel)}/webhooks/${webhookId}/test`
    );
  }

  /**
   * Rotate webhook secret
   * @param tel - Phone number
   * @param webhookId - Webhook ID
   */
  async rotateSecret(tel: string, webhookId: string): Promise<{
    id: string;
    name: string;
    secret: string;
  }> {
    const response = await this.client['post']<{
      success: boolean;
      data: { id: string; name: string; secret: string };
    }>(`/api/sessions/${encodeURIComponent(tel)}/webhooks/${webhookId}/rotate-secret`);
    
    return response.data;
  }

  /**
   * Get webhook logs and statistics
   * @param tel - Phone number
   * @param webhookId - Webhook ID
   */
  async getLogs(tel: string, webhookId: string): Promise<{
    name: string;
    url: string;
    statistics: Webhook['statistics'];
  }> {
    const response = await this.client['get']<{
      success: boolean;
      data: {
        name: string;
        url: string;
        statistics: Webhook['statistics'];
      };
    }>(`/api/sessions/${encodeURIComponent(tel)}/webhooks/${webhookId}/logs`);
    
    return response.data;
  }

  /**
   * Toggle webhook active status
   * @param tel - Phone number
   * @param webhookId - Webhook ID
   * @param active - Active status
   */
  async toggleActive(tel: string, webhookId: string, active: boolean): Promise<Webhook> {
    return this.update(tel, webhookId, { active });
  }

  /**
   * Bulk create webhooks for multiple events
   * @param tel - Phone number
   * @param url - Webhook URL
   * @param events - Array of events to subscribe to
   * @param options - Optional webhook configuration
   */
  async createForEvents(
    tel: string,
    url: string,
    events: string[],
    options?: {
      name?: string;
      headers?: Record<string, string>;
      retryConfig?: {
        maxAttempts: number;
        backoffMultiplier: number;
        initialDelay: number;
      };
    }
  ): Promise<Webhook & { secret: string }> {
    return this.create(tel, {
      name: options?.name || `Webhook for ${events.join(', ')}`,
      url,
      events,
      headers: options?.headers,
      retryConfig: options?.retryConfig,
    });
  }

  /**
   * Quick setup for message events
   * @param tel - Phone number
   * @param url - Webhook URL
   * @param name - Optional webhook name
   */
  async setupMessageWebhook(
    tel: string,
    url: string,
    name?: string
  ): Promise<Webhook & { secret: string }> {
    return this.createForEvents(tel, url, ['message.received'], {
      name: name || 'Message Webhook',
    });
  }

  /**
   * Quick setup for session events
   * @param tel - Phone number
   * @param url - Webhook URL
   * @param name - Optional webhook name
   */
  async setupSessionWebhook(
    tel: string,
    url: string,
    name?: string
  ): Promise<Webhook & { secret: string }> {
    return this.createForEvents(tel, url, [
      'session.connected',
      'session.disconnected',
      'session.qr'
    ], {
      name: name || 'Session Events Webhook',
    });
  }
}